import { useState } from "react";
import { RotateCcw, Download, Plus } from "lucide-react";
import { clsx } from "clsx";
import type { Backup } from "@/types";
import { RestoreDialog } from "@/components/backup/RestoreDialog";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

interface BackupListProps {
  backups: Backup[];
  isLoading: boolean;
  onRestore: (backupId: number) => void;
  onCreateBackup?: (vmId: number) => void;
}

function formatBytes(bytes: number | null): string {
  if (!bytes || bytes === 0) return "—";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

const statusColors: Record<string, string> = {
  completed: "bg-green-500/20 text-green-400",
  running: "bg-blue-500/20 text-blue-400",
  failed: "bg-red-500/20 text-red-400",
  pending: "bg-yellow-500/20 text-yellow-400",
  expired: "bg-gray-500/20 text-gray-400",
};

export function BackupList({
  backups,
  isLoading,
  onRestore,
  onCreateBackup,
}: BackupListProps) {
  const [restoreTarget, setRestoreTarget] = useState<Backup | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (backups.length === 0) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-gray-400">No backups found.</p>
        <p className="mt-1 text-sm text-gray-500">
          Backups are created automatically based on retention policies.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-900/50">
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                VM ID
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Status
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Type
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Size
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Date
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-400">
                Retention
              </th>
              <th className="px-4 py-3 text-right font-medium text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {backups.map((backup) => (
              <tr
                key={backup.id}
                className="transition-colors hover:bg-gray-900/30"
              >
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-200">
                    VMID: {backup.vm_id}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={clsx(
                      "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
                      statusColors[backup.status] ?? statusColors.pending
                    )}
                  >
                    {backup.status}
                  </span>
                </td>
                <td className="px-4 py-3 capitalize text-gray-300">
                  {backup.type}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-300">
                  {formatBytes(backup.size_bytes)}
                </td>
                <td className="px-4 py-3 text-gray-300">
                  {new Date(backup.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-gray-300">
                  {backup.retention_days} days
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {backup.status === "completed" && (
                      <button
                        onClick={() => setRestoreTarget(backup)}
                        title="Restore"
                        className="rounded-lg p-1.5 text-blue-400 transition-colors hover:bg-blue-500/10"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    )}
                    {backup.status === "completed" && (
                      <button
                        disabled
                        title="Coming soon"
                        className="rounded-lg p-1.5 text-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                    {onCreateBackup && (
                      <button
                        onClick={() => onCreateBackup(backup.vm_id)}
                        title="New backup for this VM"
                        className="rounded-lg p-1.5 text-green-400 transition-colors hover:bg-green-500/10"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RestoreDialog
        open={!!restoreTarget}
        backup={restoreTarget}
        onClose={() => setRestoreTarget(null)}
        onConfirm={() => {
          if (restoreTarget) {
            onRestore(restoreTarget.id);
            setRestoreTarget(null);
          }
        }}
      />
    </>
  );
}
